/**
 * Design Agent Chat Component
 *
 * Handles the chat interface for the Design Agent in FedRamp projects.
 * Replaces the previous brainstorm-based design mode with a dedicated
 * Design Agent that generates HTML based on user requests.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';

import api, { authenticatedFetch, getApiOrigin } from '../utils/axios';
import { formatTimestamp } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { ChatMessage } from './ChatMessage/ChatMessage';
import { ChatInput } from './ChatInput/ChatInput';
import { useDesignWebSocket } from '../hooks/useDesignWebSocket';
import { DesignWebSocketMessage } from '../services/designWebsocketService';
import { Icon } from './Icon/Icon';
import { IconName } from './Icon/Icon.type';
import { Button } from './Button/Button';
import { IfHasPermission } from './IfHasPermission';
import { ReadOnlyProjectNotice } from './ReadOnlyProjectNotice';
import { ModelPreference } from './ModelSelector/ModelSelector.type';
import
{
  Permissions,
  WELCOME_MESSAGE_ID,
  WELCOME_MESSAGE_TAP,
  WELCOME_MESSAGE_SERVICE_NOW,
  WELCOME_MESSAGE_POWERAPPS,
  WELCOME_MESSAGE_SALESFORCE,
} from '../constants';

import type { ITokenUsage, IDevelopmentTiming } from './TokenUsageTooltip/TokenUsageTooltip.type';
import { PlatformType } from '../types/project';

export interface DesignAgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: number;
  user_info?: any;
  // Token usage tracking (for assistant messages)
  token_usage?: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_tokens: number;
    cache_read_tokens: number;
    total_tokens: number;
    costs: {
      input_cost: number;
      output_cost: number;
      cache_creation_cost: number;
      cache_read_cost: number;
      total_cost: number;
    };
  };
  total_iterations?: number;
  model?: string;
  model_preference?: 'medium' | 'high';
  duration_seconds?: number; // Time taken for the request
  // Thinking/streaming state
  isThinking?: boolean;
  thinkingMessage?: string;
  isStreaming?: boolean;
}

interface DesignAgentChatProps {
  projectId: string;
  onHtmlGenerated?: (htmlPath: string) => void;
  project?: { permissions?: string[]; platform_type?: string } | null;
  onLoadingChange?: (loading: boolean) => void;
  onCostUpdate?: (cost: number) => void;
  shouldAutoStartDesign?: boolean;
  onAutoStartConsumed?: () => void;
}

// Map DesignAgentMessage token_usage to ITokenUsage format
const mapDesignAgentTokenUsage = (message: DesignAgentMessage): ITokenUsage | undefined => {
  if (!message.token_usage) return undefined;

  return {
    inputTokens: message.token_usage.input_tokens,
    outputTokens: message.token_usage.output_tokens,
    cacheCreationInputTokens: message.token_usage.cache_creation_tokens,
    cacheReadInputTokens: message.token_usage.cache_read_tokens,
    costs: message.token_usage.costs ? {
      input_cost: message.token_usage.costs.input_cost,
      output_cost: message.token_usage.costs.output_cost,
      cache_write_cost: message.token_usage.costs.cache_creation_cost,
      cache_read_cost: message.token_usage.costs.cache_read_cost,
      total_cost: message.token_usage.costs.total_cost,
    } : undefined
  };
};

// Map DesignAgentMessage timing to IDevelopmentTiming format
const mapDesignAgentTiming = (message: DesignAgentMessage): IDevelopmentTiming | undefined => {
  if (!message.duration_seconds && !message.total_iterations) return undefined;

  return {
    duration_seconds: message.duration_seconds || 0,
    model_interactions: message.total_iterations,
  };
};



const DesignAgentChat: React.FC<DesignAgentChatProps> = ({ projectId, onHtmlGenerated, project, onLoadingChange, onCostUpdate, shouldAutoStartDesign, onAutoStartConsumed }) => {
  const isServiceNow = project?.platform_type === PlatformType.ServiceNow;
  const isPowerApps = project?.platform_type === PlatformType.PowerApps;
  const isSalesforce = project?.platform_type === PlatformType.Salesforce;
  const [messages, setMessages] = useState<DesignAgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false);
  
  const [model, setModelState] = useState<ModelPreference>(() => {
    if (!projectId) return ModelPreference.MEDIUM;
    const storageKey = `model_preference_${projectId}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return stored as ModelPreference;
    }
    return ModelPreference.MEDIUM;
  });

  const setModel = (newModel: ModelPreference) => {
    if (!projectId) return;
    setModelState(newModel);
    const storageKey = `model_preference_${projectId}`;
    localStorage.setItem(storageKey, newModel);
  };

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Scroll debouncing ref
  const scrollTimeoutRef = useRef<number | undefined>();

  // Ref to track current isLoading state to avoid closure issues in WebSocket callbacks
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Polling fallback refs (when WebSocket disconnects)
  const isPollingRef = useRef(false);
  const pollingTimeoutRef = useRef<number | null>(null);

  // Ref to prevent multiple simultaneous message loads
  const isLoadingMessagesRef = useRef(false);
  // Ref to track if a refresh was requested while loading
  const pendingRefreshRef = useRef(false);
  // Ref to track if auto-start message has been consumed (prevents re-firing)
  const autoStartConsumedRef = useRef(false);

  // Get user for ChatMessage component
  const { user } = useAuth();

  // WebSocket for real-time multi-window sync
  const {
    connectToProject,
    disconnect: disconnectWebSocket,
    socketState,
    isReconnecting,
  } = useDesignWebSocket();

  // Check streaming status on mount and after WebSocket connects
  const checkStreamingStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      console.log('🔍 [DesignAgentChat] Checking streaming status on mount/reconnect');
      const response = await authenticatedFetch(
        `/api/projects/${projectId}/design-agent/streaming-status`
      );
      const data = await response.json();

      console.log('📊 [DesignAgentChat] Streaming status:', data);

      if (data.is_streaming && data.status === 'active') {
        // Still streaming - show "Working..." message
        setMessages(prev => {
          // Find the last assistant message
          let lastAssistantIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].role === 'assistant') {
              lastAssistantIndex = i;
              break;
            }
          }
          
          if (lastAssistantIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[lastAssistantIndex] = {
              ...updatedMessages[lastAssistantIndex],
              isThinking: true,
              thinkingMessage: 'Designing...',
              isStreaming: true
            };
            return updatedMessages;
          } else {
            // Fallback: Create new message if no assistant message exists
            const streamingMessage: DesignAgentMessage = {
              id: `streaming-${data.assistant_message_id || Date.now()}`,
              role: 'assistant',
              content: '',
              created_at: Date.now() / 1000,
              isThinking: true,
              thinkingMessage: 'Designing...',
              isStreaming: true
            };
            return [...prev, streamingMessage];
          }
        });
        
        setIsLoading(true);
        
        // Poll again after 5 seconds (optimized for performance)
        if (isPollingRef.current) {
          pollingTimeoutRef.current = window.setTimeout(() => checkStreamingStatus(), 5000);
        }
        
      } else if (data.status === 'completed' || data.status === 'cancelled') {
        // Streaming completed or cancelled - reload messages to get the final response
        console.log('✅ [DesignAgentChat] Streaming completed, refreshing messages');
        setIsLoading(false);
        fetchMessageHistory();
        isPollingRef.current = false; // Stop polling
        
      } else if (data.status === 'error') {
        // Error occurred
        console.error('❌ [DesignAgentChat] Streaming error:', data.error_message);
        setIsLoading(false);
        isPollingRef.current = false; // Stop polling
        
        // Show error message
        setMessages(prev => {
          const withoutThinking = prev.filter(msg => !msg.isThinking);
          const errorMsg: DesignAgentMessage = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: data.error_message || 'An error occurred during generation.',
            created_at: Date.now() / 1000,
            isThinking: false
          };
          return [...withoutThinking, errorMsg];
        });
        
      } else {
        // No active streaming detected via API
        console.log('📡 [DesignAgentChat] checkStreamingStatus: No active streaming detected, status=', data.status);
        setIsLoading(false);
        isPollingRef.current = false; // Stop polling
      }
    } catch (error) {
      console.error('❌ [DesignAgentChat] Error checking streaming status:', error);
      setIsLoading(false);
      isPollingRef.current = false; // Stop polling on error
    }
  }, [projectId]);

  // WebSocket message handler for multi-window sync
  const handleDesignWebSocketMessage = useCallback((message: DesignWebSocketMessage) => {
    console.log('📨 [DesignAgentChat] WebSocket message:', message.type, message);

    switch (message.type) {
      case 'design_user_message_added': {
        // Add user message from other window (with deduplication)
        const incomingContent = message.data.content;

        setMessages(prev => {
          // Find the last user message and check if it has the same content
          const lastUserMessage = [...prev].reverse().find(m => m.role === 'user');

          // If this exact message ID is already in the array, skip to prevent duplicate WS deliveries
          if (prev.some(m => m.id === message.data.message_id)) {
            return prev;
          }

          // If last user message has a temporary ID and matching content, it's an optimistic update from THIS client.
          // Update its ID to the real database ID so future fetches and deduplications work correctly.
          // We MUST NOT skip broadcasts just because content matches, as users might send the exact same text again.
          if (lastUserMessage && lastUserMessage.id.startsWith('temp-user-') && lastUserMessage.content === incomingContent) {
            console.log('🔄 [DesignAgentChat] Optimistic user message found, updating ID to real DB ID');
            return prev.map(m => m.id === lastUserMessage.id ? { ...m, id: message.data.message_id || m.id } : m);
          }

          // Add new message from WebSocket (different window)
          const newUserMessage: DesignAgentMessage = {
            id: message.data.message_id || Date.now().toString(),
            role: 'user',
            content: incomingContent || '',
            created_at: Date.now() / 1000,
            user_info: message.data.user_info
          };

          console.log('✅ [DesignAgentChat] Adding user message from WebSocket');
          
          return [...prev, newUserMessage];
        });
        break;
      }

      case 'design_streaming_started': {
        // Streaming started (possibly from another window)
        console.log('🔄 [DesignAgentChat] Streaming started');

        // Use ref to check current loading state (avoid closure issues)
        if (isLoadingRef.current) {
          console.log('📡 [DesignAgentChat] Already loading, skipping');
          return;
        }

        console.log('📡 [DesignAgentChat] Setting isLoading=true');
        setIsLoading(true);
        setElapsedSeconds(0);

        // Add "Working..." indicator if not already present (for passive windows)
        setMessages(prev => {
          const hasThinking = prev.some(m => m.role === 'assistant' && !m.content && prev.indexOf(m) === prev.length - 1);
          if (hasThinking) {
            return prev;
          }
          const thinkingMessage: DesignAgentMessage = {
            id: `streaming-${message.data.assistant_message_id || Date.now()}`,
            role: 'assistant',
            content: '',
            created_at: Date.now() / 1000,
          };
          
          return [...prev, thinkingMessage];
        });
        break;
      }

      case 'design_streaming_completed':
        console.log('✅ [DesignAgentChat] Streaming completed');
        setIsLoading(false);
        setElapsedSeconds(0);
        
        // Reload messages to get the completed response
        fetchMessageHistory();

        // Refresh project cost
        if (projectId && onCostUpdate) {
          api.get(`/projects/${projectId}/stats`).then(response => {
            const totalCost = parseFloat(response.data.total_cost) || 0;
            onCostUpdate(totalCost);
          }).catch(err => {
            console.error('Failed to fetch project cost:', err);
          });
        }

        // Notify parent component about HTML generation
        if (onHtmlGenerated) {
          console.log('✅ [DesignAgentChat] WebSocket completion triggering preview refresh');
          onHtmlGenerated('');
        }
        break;

      case 'design_streaming_cancelled':
        console.log('⚠️ [DesignAgentChat] Streaming cancelled');
        setIsLoading(false);
        setElapsedSeconds(0);
        
        fetchMessageHistory();
        break;

      case 'design_streaming_cost_update_on_cancellation':
        console.log('⚠️ [DesignAgentChat] Streaming cost update on cancellation');
        // Reload messages to get updated token_usage data for stopped message
        fetchMessageHistory();
        // Refresh project cost
        if (projectId && onCostUpdate) {
          api.get(`/projects/${projectId}/stats`).then(response => {
            const totalCost = parseFloat(response.data.total_cost) || 0;
            onCostUpdate(totalCost);
          }).catch(err => {
            console.error('Failed to fetch project cost:', err);
          });
        }
        break;

    }
  }, [hasUserScrolledUp]);

  // 🔄 CLEANUP POLLING ON UNMOUNT OR PROJECT CHANGE
  useEffect(() => {
    // Cleanup: stop polling when project changes or component unmounts
    return () => {
      isPollingRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [projectId]);

  // Connect to WebSocket on mount
  useEffect(() => {
    if (projectId) {
      console.log('🔌 [DesignAgentChat] Connecting to WebSocket for project:', projectId);
      connectToProject(projectId, {
        onMessage: handleDesignWebSocketMessage,
        onConnect: () => {
          console.log('✅ [DesignAgentChat] WebSocket connected');
          // Stop polling when WebSocket connects
          isPollingRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          // Check streaming status after connection
          checkStreamingStatus();
        },
        onReconnect: () => {
          console.log('🔄 [DesignAgentChat] WebSocket reconnected, reloading messages');
          // Stop polling and reload messages to catch up
          isPollingRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          // Check streaming status and reload messages after reconnect
          checkStreamingStatus();
          fetchMessageHistory();
        },
        onDisconnect: () => {
          console.log('⚠️ [DesignAgentChat] WebSocket disconnected');
          // Start polling fallback if still streaming
          if (isLoading) {
            isPollingRef.current = true;
            checkStreamingStatus();
          }
        },
        onError: (error) => {
          console.error('📡 [DesignAgentChat] WebSocket Error:', error);
          // Start polling fallback if still streaming
          if (isLoading) {
            isPollingRef.current = true;
            checkStreamingStatus();
          }
        }
      });
    }

    return () => {
      // Don't disconnect here - let the hook manage its own lifecycle
    };
  }, [projectId, connectToProject, handleDesignWebSocketMessage, checkStreamingStatus, isLoading]);

  // Check streaming status on mount
  useEffect(() => {
    if (projectId) {
      console.log('🚀 [DesignAgentChat] Component mounted, checking for active streaming');
      checkStreamingStatus();
    }
  }, [projectId, checkStreamingStatus]);

  useEffect(() => {
    fetchMessageHistory(true); // Pass true for initial load
  }, [projectId]);

  // Load more messages handler for pagination
  const loadMore = useCallback(async (container: HTMLDivElement) => {
    if (isLoadingRef.current || isLoadingMore) return;

    // Check if we should load more
    if (!hasMoreMessages || !projectId) return;

    // Get the oldest message ID before starting the load
    const oldestMessage = messages[0];
    if (!oldestMessage || !oldestMessage.id) {
      console.warn('⚠️ No oldest message found for pagination');
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingMore(true);

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      // Call fetchMessageHistory directly with the oldest message ID
      await fetchMessageHistory(false, 10, oldestMessage.id);
    } finally {
      // Preserve scroll position after new messages are prepended
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        isLoadingRef.current = false;
        setIsLoadingMore(false);
      });
    }
  }, [hasMoreMessages, projectId, isLoadingMore, messages]); // fetchMessageHistory is defined below, so we can't include it here


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle scroll to detect if user is at bottom and trigger load more
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Check if user is at bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 50; // pixels from bottom

    const atBottom = distanceFromBottom <= threshold;
    setIsAtBottom(atBottom);

    // If user scrolled up manually (away from bottom), set the flag
    if (!atBottom && !hasUserScrolledUp) {
      setHasUserScrolledUp(true);
    }

    // If user scrolled back to bottom manually, re-enable auto-scroll
    if (atBottom && hasUserScrolledUp) {
      setHasUserScrolledUp(false);
    }


    // Debounce the scroll checks for load more
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }

    const SCROLL_THRESHOLD = 100; // pixels from top to trigger load more

    scrollTimeoutRef.current = window.setTimeout(() => {
      if (scrollTop < SCROLL_THRESHOLD && !isLoadingRef.current) {
        // Check if we should load more
        if (hasMoreMessages && !isLoadingMore) {
          loadMore(container);
        }
      }
    }, 80);
  }, [hasUserScrolledUp, hasMoreMessages, isLoadingMore, loadMore]);

  // Auto-scroll only if user hasn't manually scrolled up
  useEffect(() => {
    if (!hasUserScrolledUp) {
      scrollToBottom();
    }
  }, [messages, hasUserScrolledUp]);

  const fetchMessageHistory = async (isInitialLoad = false, limit = 10, beforeMessageId?: string) => {
    // If already loading, mark that we need to refresh again after current fetch
    if (isLoadingMessagesRef.current) {
      pendingRefreshRef.current = true;
      return;
    }

    isLoadingMessagesRef.current = true;
    pendingRefreshRef.current = false;

    try {
      // Only show full-screen loading for initial load (not for pagination)
      if (isInitialLoad && !beforeMessageId) {
        setLoadingHistory(true);
        onLoadingChange?.(true);
      }

      // Build query parameters for pagination
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (beforeMessageId) params.append('before_message_id', beforeMessageId);

      const response = await api.get(`/projects/${projectId}/design-agent/history?${params.toString()}`);
      const newMessages = response.data.messages || [];

      // Update hasMore state based on pagination metadata
      setHasMoreMessages(response.data?.pagination?.has_more || false);

      // Handle pagination: if beforeMessageId is provided, prepend to existing messages
      if (beforeMessageId) {
        // This is a pagination request - prepend older messages
        setMessages(prevMessages => {
          const hasMore = response.data?.pagination?.has_more || false;
          if (!hasMore && project?.platform_type) {
            const hasWelcome = prevMessages.some(msg => msg.id === WELCOME_MESSAGE_ID);
            if (!hasWelcome) {
              const welcomeMessage: DesignAgentMessage = {
                id: WELCOME_MESSAGE_ID,
                role: 'assistant',
                content: isServiceNow ? WELCOME_MESSAGE_SERVICE_NOW : isPowerApps ? WELCOME_MESSAGE_POWERAPPS : isSalesforce ? WELCOME_MESSAGE_SALESFORCE : WELCOME_MESSAGE_TAP,
                created_at: Date.now() / 1000,
              };
              return [welcomeMessage, ...newMessages, ...prevMessages];
            }
          }
          return [...newMessages, ...prevMessages];
        });
      } else {
        // This is an initial load or refresh - replace all messages
        // Just set messages - checkStreamingStatus will handle marking empty messages as thinking
        const hasMore = response.data?.pagination?.has_more || false;
        const shouldAddWelcome = newMessages.length === 0 || !hasMore;
        
        setMessages(prevMessages => {
          // Find any temporary optimistic messages currently on the screen.
          // We only preserve them if actively streaming to prevent shadowing actual history.
          const optimisticMessages = isLoadingRef.current ? prevMessages.filter(m => 
            m.id.startsWith('temp-user-') || 
            m.id.startsWith('temp-assistant-') || 
            m.id.startsWith('streaming-')
          ) : [];
          
          if (shouldAddWelcome && project?.platform_type) {
            const welcomeMessage: DesignAgentMessage = {
              id: WELCOME_MESSAGE_ID,
              role: 'assistant',
              content: isServiceNow ? WELCOME_MESSAGE_SERVICE_NOW : isPowerApps ? WELCOME_MESSAGE_POWERAPPS : isSalesforce ? WELCOME_MESSAGE_SALESFORCE : WELCOME_MESSAGE_TAP,
              created_at: Date.now() / 1000,
            };
            return [welcomeMessage, ...newMessages, ...optimisticMessages];
          } else {
            return [...newMessages, ...optimisticMessages];
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch design agent history:', error);
    } finally {
      // Only hide full-screen loading for initial load
      if (isInitialLoad && !beforeMessageId) {
        setLoadingHistory(false);
        onLoadingChange?.(false);
      }
      isLoadingMessagesRef.current = false;

      // If a refresh was requested while we were loading, do it now
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        fetchMessageHistory();
      }
    }
  };

  const handleStopStreaming = async () => {
    console.log('🛑 Stopping design agent conversation...');

    // Abort the fetch request
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    if (projectId) {
      try {
        await api.post(`/projects/${projectId}/design-agent/stop`);

        // Reload messages to get the stop message from database
        console.log('📥 Reloading design agent messages after stop...');
        await fetchMessageHistory();
      } catch (error) {
        console.error('❌ Failed to stop design agent stream:', error);
      }
    }

    setIsLoading(false);
    setElapsedSeconds(0);
    console.log('✅ Design agent conversation stopped');
  };

  const handleSendMessage = async (messageContent?: string) => {
    const userMessage = (messageContent || input).trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setIsLoading(true);
    setElapsedSeconds(0);

    // Always scroll to bottom when user sends a new message
    setHasUserScrolledUp(false);

    // Create AbortController for this request
    const controller = new AbortController();
    setAbortController(controller);

    // Generate unique IDs for this conversation turn
    const userMessageId = `temp-user-${Date.now()}`;
    const assistantMessageId = `temp-assistant-${Date.now()}`;

    // Optimistically add user message
    const tempUserMessage: DesignAgentMessage = {
      id: userMessageId,
      role: 'user',
      content: userMessage,
      created_at: Date.now() / 1000,
    };

    // Add thinking message for assistant
    const thinkingMessage: DesignAgentMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      created_at: Date.now() / 1000,
    };
    setMessages(prev => [...prev,  tempUserMessage, thinkingMessage]);

    // Setup timeout for the entire request (15 minutes)
    const requestTimeout = 900000; // 15 minutes in milliseconds
    const timeoutId = setTimeout(() => {
      console.error('Request timeout after 15 minutes');
    }, requestTimeout);

    try {
      // Create EventSource for SSE streaming
      const baseURL = getApiOrigin();

      // Make POST request to get SSE stream using authenticatedFetch
      // This will automatically handle 401 responses and redirect to login
      const response = await authenticatedFetch(`${baseURL}/api/projects/${projectId}/design-agent/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: userMessage,
          type: 'design',
          model_preference: model
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is null');
      }

      let buffer = '';
      let lastActivityTime = Date.now();
      const activityTimeout = 900000; // 15 minutes of no activity = timeout
      const requestStartTime = Date.now();
      let heartbeatCount = 0;

      console.log('🚀 Starting Design Agent request stream...');

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('✅ Stream completed');
          console.log(`📊 Total heartbeats received: ${heartbeatCount}`);
          break;
        }

        // Update last activity time
        const previousActivityTime = lastActivityTime;
        lastActivityTime = Date.now();
        const timeSinceLastActivity = lastActivityTime - previousActivityTime;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // Handle SSE comment lines (heartbeats)
          if (line.startsWith(':')) {
            heartbeatCount++;
            const elapsed = Math.floor((Date.now() - requestStartTime) / 1000);
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            console.log(`💓 Heartbeat #${heartbeatCount} - Connection alive (${minutes}:${seconds.toString().padStart(2, '0')})`);
            continue; // Skip to next line
          }

          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));

            // Handle ACTION messages from DataModelBuilder API (show in thinking indicator)
            if (data.type === 'ACTION' || data.type === 'TOOL_CALL') {
              console.log(`📊 ${data.type}:`, data.message);
              // Update the thinking message with the current action
              setMessages(prev => 
                prev.map(msg => {
                  if (msg.id === thinkingMessage.id) {
                    return { ...msg, thinkingMessage: data.message };
                  } else {
                    return msg;
                  }
                })
              );
            }
            // Handle heartbeat messages to show progress
            else if (data.status === 'generating') {
              if (data.elapsed_seconds !== undefined) {
                setElapsedSeconds(data.elapsed_seconds);
              }
            }
            // Process final response
            else if (data.status === 'complete') {
              // Remove temp user message and thinking message, add actual messages from backend
              setMessages(prev => {
                const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id && m.id !== thinkingMessage.id);

                // Add user message (already saved in backend) - preserve original timestamp
                const userMsg: DesignAgentMessage = {
                  id: `user-${Date.now()}`,
                  role: 'user',
                  content: userMessage,
                  created_at: tempUserMessage.created_at, // Use the original timestamp from temp message
                };

                // Add assistant response with token usage data
                const assistantMsg: DesignAgentMessage = {
                  id: data.message_id,
                  role: 'assistant',
                  content: data.content,
                  created_at: data.created_at,
                  token_usage: data.token_usage,
                  total_iterations: data.total_iterations,
                  model: data.model,
                  duration_seconds: data.duration_seconds
                };

                return [...withoutTemp, userMsg, assistantMsg];
              });

              // Notify parent component about HTML generation (only for successful responses)
              // HTML is always saved to the same location for the project
              if (data.status === 'complete') {
                // Logic moved to WebSocket handler 'design_streaming_completed'
                // to avoid double triggering and ensure reliability
              }
            } else if (data.status === 'error') {
              // Handle error response - treat it as a message (already saved to DB)

              setMessages(prev => {
                const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id && m.id !== thinkingMessage.id);

                // Add user message (preserve original timestamp)
                const userMsg: DesignAgentMessage = {
                  id: `user-${Date.now()}`,
                  role: 'user',
                  content: userMessage,
                  created_at: tempUserMessage.created_at,
                };

                // Add error message
                const errorMsg: DesignAgentMessage = {
                  id: data.message_id || `error-${Date.now()}`,
                  role: 'assistant',
                  content: data.error || data.content || 'Failed to process your request',
                  created_at: data.created_at || Date.now() / 1000,
                };

                return [...withoutTemp, userMsg, errorMsg];
              });

              // Don't throw - just display the error message
              setIsLoading(false);
              setElapsedSeconds(0);
              return;
            }
          }
        }

        // Check for activity timeout
        if (Date.now() - lastActivityTime > activityTimeout) {
          throw new Error('Connection timeout - no activity for 15 minutes');
        }
      }

      // Clear the timeout if request completes successfully
      clearTimeout(timeoutId);

    } catch (error: any) {
      console.error('Failed to send design agent message:', error);
      clearTimeout(timeoutId);

      // Don't show error message if request was aborted by user
      if (error.name === 'AbortError') {
        console.log('✅ Design agent request was aborted by user');
        // Remove temp user message and thinking message on abort
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id && m.id !== thinkingMessage.id));
      } else {
        // Add error message (without "Error: " prefix)
        const errorMsg: DesignAgentMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: error.message || 'Failed to process your request. Please try again.',
          created_at: Date.now() / 1000,
        };

        setMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id && m.id !== thinkingMessage.id);
          return [...withoutTemp, errorMsg];
        });
      }
    } finally {
      setIsLoading(false);
      setElapsedSeconds(0);
      setAbortController(null);
    }
  };

  // Auto-send design message from CAB-to-Design transition.
  // Defers the send by a short delay so mount-time async operations
  // (checkStreamingStatus, WebSocket connect, fetchMessageHistory) settle
  // before we inject the message. The prompt is NOT shown in the input box —
  // it simulates a direct send as if the user clicked the send button.
  useEffect(() => {
    if (
      shouldAutoStartDesign &&
      !loadingHistory &&
      !isLoading &&
      !autoStartConsumedRef.current
    ) {
      autoStartConsumedRef.current = true;
      const prompt = `\`\`\`CAB-TO-DESIGN\`\`\``;

      // Defer the send so the component has settled after mount.
      // Call onAutoStartConsumed inside the timeout so the parent
      // state change doesn't trigger effect cleanup before the send fires.
      setTimeout(() => {
        handleSendMessage(prompt);
        onAutoStartConsumed?.();
      }, 150);
    }
  }, [shouldAutoStartDesign, loadingHistory, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the consumed ref when shouldAutoStartDesign changes to a new value
  useEffect(() => {
    if (!shouldAutoStartDesign) {
      autoStartConsumedRef.current = false;
    }
  }, [shouldAutoStartDesign]);

  // Show loading state - parent will handle skeleton rendering
  if (loadingHistory) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-bg-primary">
      {/* Messages Area Wrapper */}
      <div className="flex-1 min-h-0 relative">
      <div ref={chatContainerRef} onScroll={handleScroll} className="h-full flex flex-col items-center py-bg-v2-md overflow-y-auto modern-scrollbar-spaced">
        <div className="flex flex-col gap-bg-v2-xl w-full max-w-[800px] relative px-4">
          {/* Messages */}
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isStreamingMessage = isLastMessage && isLoading && message.role === 'assistant';

            return (
              <div
                key={message.id}
                className="animate-fade-in-up-smooth opacity-0"
                style={{
                  animationDelay: `${index * 0.05}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <ChatMessage
                  message={{
                    ...message,
                    timestamp: formatTimestamp(new Date(message.created_at * 1000), {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true
                    }) as any,
                    tokenUsage: mapDesignAgentTokenUsage(message),
                    developmentTiming: mapDesignAgentTiming(message),
                    isThinking: isStreamingMessage && !message.content,
                    thinkingMessage: isStreamingMessage && !message.content ? (message.thinkingMessage || 'Designing...') : undefined,
                    isStreaming: isStreamingMessage && message.content.length > 0,
                  }}
                  currentUser={user}
                  project={null}
                  isDesignMode={true}
                  isFullWidthMessages={false}
                />
              </div>
            );
          })}


          <div ref={messagesEndRef} />
        </div>

      </div>

        {/* Scroll to Bottom Button */}
        {!isAtBottom && (
          <div className="absolute bottom-bg-v2-sm left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <Button
              onClick={() => {
                setHasUserScrolledUp(false);
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              variant="ghost"
              className="!w-9 !h-9 !rounded-bg-v2-full !bg-bg-primary border border-border-secondary !shadow-bg-v2-md hover:!bg-bg-secondary pointer-events-auto !p-bg-v2-none"
              title="Scroll to bottom"
              label={<Icon name={IconName.ArrowDown} size="sm" variant="secondary" />}
            />
          </div>
        )}
      </div>

      {/* Chat Input */}
      <IfHasPermission
        permissions={project?.permissions}
        requires={Permissions.UpdateProjectPermission}
        fallback={<ReadOnlyProjectNotice className="mx-bg-v2-xl mb-bg-v2-3xl" />}
      >
        <div className="flex justify-center pt-bg-v2-lg pb-bg-v2-3xl px-bg-v2-xl bg-bg-primary">
          <ChatInput
            value={input}
            onChange={setInput}
            onSend={handleSendMessage}
            onStop={handleStopStreaming}
            placeholder={"Describe what you want to prototype..."}
            disabled={isLoading}
            isStreaming={isLoading}
            isSending={isLoading}
            projectId={projectId}
            showAttachmentButton={false}
            showModelSelector={true}
            model={model}
            onModelChange={setModel}
          />
        </div>
      </IfHasPermission>
    </div>
  );
};

export default DesignAgentChat;
